import React from 'react';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { MPFIconButton } from 'volto-ministerio-publico-federal/components/UI';

import { Miniatura } from './Miniatura';
import {
  fatiaDaPagina,
  gradePagina,
  GRADE_COLUNAS_PADRAO,
  totalDePaginas,
  type GaleriaFoto,
} from './helpers';

interface GradeProps {
  fotos: GaleriaFoto[];
  /** Foto em cartaz — fica marcada com `aria-current` na grade. */
  indice: number;
  colunas?: number;
  /** Na grade o clique abre a foto ampliada, sem passar pelo palco. */
  onSelecionar: (indice: number) => void;
}

/* As classes precisam existir literais no código para o Tailwind gerá-las:
   `lg:grid-cols-${n}` seria descartado na varredura do `content`. */
const COLUNAS_CLASS: Record<number, string> = {
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
};

/**
 * O modo "Ver todas": substitui palco e trilha dentro do mesmo cartão — não é
 * modal. Acima de 24 fotos pagina de 12 em 12, para a grade não virar uma
 * parede de imagens que empurra o resto da página para fora da tela.
 *
 * A página volta a zero sempre que a galeria muda de tamanho: sem isso, tirar
 * fotos no editor deixaria o leitor parado numa página que não existe mais.
 */
export const Grade: React.FC<GradeProps> = ({
  fotos,
  indice,
  colunas = GRADE_COLUNAS_PADRAO,
  onSelecionar,
}) => {
  const [pagina, setPagina] = React.useState(0);
  const paginas = totalDePaginas(fotos.length);

  React.useEffect(() => {
    setPagina(0);
  }, [fotos.length]);

  const visiveis = fatiaDaPagina(fotos, pagina);
  const colunasClass =
    COLUNAS_CLASS[colunas] ?? COLUNAS_CLASS[GRADE_COLUNAS_PADRAO];

  return (
    <div className="mpf-galeria__grade-area">
      <ul className={`mpf-galeria__grade ${colunasClass}`}>
        {visiveis.map((foto) => (
          <li key={foto.key} className="mpf-galeria__grade-item">
            <Miniatura
              foto={foto}
              onSelecionar={onSelecionar}
              ativa={foto.indice === indice}
              verbo="Ampliar"
              className="mpf-galeria__thumb--grade"
            />
          </li>
        ))}
      </ul>

      {gradePagina(fotos.length) && paginas > 1 && (
        <nav aria-label="Páginas da galeria" className="mpf-galeria__paginacao">
          <MPFIconButton
            icon={CaretLeftIcon}
            label="Página anterior"
            shape="square"
            variant="outline"
            size="m"
            disabled={pagina === 0}
            onClick={() => setPagina((atual) => Math.max(atual - 1, 0))}
          />
          <span className="mpf-galeria__paginacao-texto">
            Página {pagina + 1} de {paginas}
          </span>
          <MPFIconButton
            icon={CaretRightIcon}
            label="Próxima página"
            shape="square"
            variant="outline"
            size="m"
            disabled={pagina >= paginas - 1}
            onClick={() =>
              setPagina((atual) => Math.min(atual + 1, paginas - 1))
            }
          />
        </nav>
      )}
    </div>
  );
};

export default Grade;
